import { Box, Container, Toolbar, useTheme } from '@mui/material';
import React from 'react';

import { useLayoutContext } from '../../../contexts';
import { LAYOUT_CONSTANTS } from '../constants/layout-constants';
import { useLayoutCalculations } from '../hooks/use-layout-calculations';

export type MainContentAreaProps = {
  children: React.ReactNode;
  isSidebarOpen: boolean;
  isNotificationPanelOpen: boolean;
};

/**
 * Main Content Area Component
 * Contains the main content area with proper spacing and responsive behavior
 */
const MainContentArea = ({ children, isSidebarOpen, isNotificationPanelOpen }: MainContentAreaProps) => {
  const theme = useTheme();
  const { layoutMode } = useLayoutContext();
  const { getMainContentWidth, getMainContentMarginLeft, getMainContentMarginRight } = useLayoutCalculations();

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard
        }),
        marginLeft: getMainContentMarginLeft(isSidebarOpen),
        marginRight: getMainContentMarginRight(isNotificationPanelOpen),
        width: getMainContentWidth(isSidebarOpen, isNotificationPanelOpen)
      }}
    >
      {layoutMode === 'constrained' ? (
        <Container maxWidth="lg">
          {/* Toolbar spacer */}
          <Toolbar sx={{ minHeight: LAYOUT_CONSTANTS.APPBAR_HEIGHT }} />

          {/* Content Container */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              backgroundColor: 'var(--bg-color)',
              borderTopLeftRadius: '1rem',
              borderTopRightRadius: '1rem',
              overflow: 'hidden'
            }}
          >
            {/* Page Content */}
            <Box
              sx={{
                flexGrow: 1,
                p: { xs: 1, sm: 2, md: 2 },
                overflow: 'auto',
                backgroundColor: 'var(--bg-color)',
                minHeight: 0 // Allow flex child to shrink
              }}
            >
              {children}
            </Box>
          </Box>
        </Container>
      ) : (
        <Box sx={{ width: '100%' }}>
          {/* Toolbar spacer */}
          <Toolbar sx={{ minHeight: LAYOUT_CONSTANTS.APPBAR_HEIGHT }} />

          {/* Full-width Content Container */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              backgroundColor: 'var(--bg-color)',
              borderTopLeftRadius: '1rem',
              borderTopRightRadius: '1rem',
              overflow: 'hidden',
              width: '100%'
            }}
          >
            {/* Page Content - Full Width */}
            <Box
              sx={{
                flexGrow: 1,
                p: { xs: 1, sm: 2, md: 2 },
                overflow: 'auto',
                backgroundColor: 'var(--bg-color)',
                minHeight: 0, // Allow flex child to shrink
                width: '100%'
              }}
            >
              {children}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MainContentArea;
